import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BulkUpload } from '@/components/users/BulkUpload';
import { UserForm } from '@/components/users/UserForm';
import { UserTable } from '@/components/users/UserTable';
import { useAdminScope } from '@/contexts/AdminScopeContext';
import { useAuth } from '@/contexts/AuthContext';
import { getFullName } from '@/lib/utils';
import { bulkCreateUsers, createUser, deleteUser, getUsers, reinviteUser, updateUser } from '@/services/userApi';
import { Role } from '@/types/auth';
import { BulkUserUploadResult, CreateUserData, UpdateUserData, UserListItem } from '@/types/user';
import { ChevronLeft, ChevronRight, Filter, Search, Upload, UserPlus, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function UsersPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { selectedTenantId } = useAdminScope();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'active' | 'pending' | 'inactive' | 'all'>('all');

  // Modal states
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const registrationStatus =
      statusFilter === 'active'
        ? ('completed' as const)
        : statusFilter === 'pending'
          ? ('pending' as const)
          : undefined;
    getUsers({
      page,
      limit: pageSize,
      search: searchQuery.trim() || undefined,
      role: roleFilter === 'all' ? undefined : roleFilter,
      registrationStatus,
      tenantId: selectedTenantId,
    })
      .then(({ users: data, pagination: pag }) => {
        if (!cancelled) {
          setUsers(data);
          setPagination({ total: pag.total, totalPages: pag.totalPages });
        }
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load users');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, searchQuery, roleFilter, statusFilter, refreshKey, selectedTenantId]);

  const loadUsers = useCallback(() => {
    setPage(1);
    setRefreshKey((k) => k + 1);
  }, []);

  const handleAddUser = () => {
    setSelectedUser(null);
    setUserFormOpen(true);
  };

  const handleEditUser = (user: UserListItem) => {
    setSelectedUser(user);
    setUserFormOpen(true);
  };

  const handleDeleteClick = (user: UserListItem) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleViewDocuments = (user: UserListItem) => {
    navigate(`/admin/users/${encodeURIComponent(user.email)}/documents`);
  };

  const handleReinvite = async (user: UserListItem) => {
    try {
      await reinviteUser(user.email, selectedTenantId);
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  const handleFormSubmit = async (data: CreateUserData | UpdateUserData) => {
    setIsSubmitting(true);
    try {
      if (selectedUser) {
        // Update existing user
        const updated = await updateUser(
          selectedUser.email,
          data,
          selectedTenantId
        );
        setUsers((prev) =>
          prev.map((u) => (u.email === selectedUser.email ? updated : u))
        );
        toast.success('User updated successfully');
      } else {
        // Create new user
        await createUser(data as CreateUserData, selectedTenantId);
        toast.success('User created successfully. Invitation email sent.');
        loadUsers();
      }
      setUserFormOpen(false);
      setSelectedUser(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save user');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      await deleteUser();
      setUsers((prev) => prev.filter((u) => u.email !== selectedUser.email));
      setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      refetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkUpload = async (file: File): Promise<BulkUserUploadResult> => {
    const result = await bulkCreateUsers(file, selectedTenantId);
    if (result.success > 0) {
      await loadUsers();
    }
    return result;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
    setPage(1);
  };

  const refetchUsers = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const hasActiveFilters = searchQuery || roleFilter !== 'all' || statusFilter !== 'all';

  const startItem = pagination.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, pagination.total);
  const canPrev = page > 1;
  const canNext = page < pagination.totalPages;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground">
            Manage users, invitations, and access permissions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkUploadOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Bulk Upload</span>
          </Button>
          <Button onClick={handleAddUser}>
            <UserPlus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Add User</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={roleFilter}
            onValueChange={(value) => {
              setRoleFilter(value as Role | 'all');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-32">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="role_user">User</SelectItem>
              <SelectItem value="role_admin">Admin</SelectItem>
              {currentUser?.role === 'role_superadmin' && (
                <SelectItem value="role_superadmin">Super Admin</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value as typeof statusFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* User Table/List */}
      {users.length === 0 && !isLoading ? (
        <EmptyState
          icon={Users}
          title="No users found"
          description={
            hasActiveFilters
              ? 'No users match your current filters.'
              : 'Get started by adding your first user.'
          }
          action={
            hasActiveFilters
              ? { label: 'Clear Filters', onClick: clearFilters }
              : { label: 'Add User', onClick: handleAddUser }
          }
        />
      ) : (
        <>
          <UserTable
            users={users}
            isLoading={isLoading}
            currentUserEmail={currentUser?.email}
            currentUserRole={currentUser?.role}
            currentUserTenantId={currentUser?.tenantId}
            onEdit={handleEditUser}
            onDelete={handleDeleteClick}
            onReinvite={handleReinvite}
            onViewDocuments={handleViewDocuments}
          />
          {pagination.totalPages > 0 && (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  Showing {startItem}–{endItem} of {pagination.total}
                </p>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">per page</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={!canPrev || isLoading}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {pagination.totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!canNext || isLoading}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* User Form Modal */}
      <UserForm
        open={userFormOpen}
        onOpenChange={setUserFormOpen}
        onSubmit={handleFormSubmit}
        user={selectedUser || undefined}
        isLoading={isSubmitting}
      />

      {/* Bulk Upload Modal */}
      <BulkUpload
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        onUpload={handleBulkUpload}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete User"
        description={`Are you sure you want to delete ${selectedUser ? getFullName(selectedUser) : ''}? This will also delete all their documents. This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        loading={isSubmitting}
      />
    </div>
  );
}
